import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import Louse from '@/assets/icons/louse.svg?component';
import { Alert } from '@/components/atoms/Alert';
import { SvgIcon } from '@/components/atoms/SvgIcon/SvgIcon';

import styles from './MissingPenNotice.module.scss';

/**
 * The key the notice used as a toast, kept so a reader who already dismissed it does not
 * see it again.
 */
export function missingPenNoticeDismissKey(userId: number | undefined): string {
    return `toastDismissed-lice-report-userId-${userId}`;
}

function readDismissed(key: string) {
    try {
        return localStorage.getItem(key) === 'true';
    } catch {
        return false;
    }
}

interface MissingPenNoticeProps {
    userId: number | undefined;
}

/**
 * Explains why a pen can be missing from the lice report. It sits below the report, so it
 * cannot push the filters down when it arrives.
 */
export function MissingPenNotice({ userId }: MissingPenNoticeProps) {
    const { t } = useTranslation('liceReport');
    const dismissKey = missingPenNoticeDismissKey(userId);
    const [dismissed, setDismissed] = useState(() => readDismissed(dismissKey));

    if (dismissed) {
        return null;
    }

    const dismiss = () => {
        setDismissed(true);
        try {
            localStorage.setItem(dismissKey, 'true');
        } catch {
            // Storage can be unavailable (private mode); the notice still closes for this visit.
        }
    };

    return (
        <Alert
            tone="info"
            className={styles.notice}
            icon={<SvgIcon icon={Louse} size="custom" customSize={20} />}
            onDismiss={dismiss}>
            {t('common.missingPenExplain')}
        </Alert>
    );
}

export default MissingPenNotice;
